import React, { useState, useEffect } from 'react';
import { ticketsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Gear, Clock, Lightning, ArrowsClockwise, CheckCircle, Play,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const TicketConfigPage = () => {
  const [slaConfig, setSlaConfig] = useState({ low: 72, medium: 48, high: 24, critical: 4 });
  const [config, setConfig] = useState({
    auto_escalate_enabled: false, auto_escalate_after_hours: 2,
    auto_close_enabled: false, auto_close_after_days: 3,
    enabled_categories: [],
  });
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [slaRes, cfgRes, catRes] = await Promise.all([
          ticketsApi.slaConfig(), ticketsApi.config(), ticketsApi.categories(),
        ]);
        setSlaConfig({ low: slaRes.data.low, medium: slaRes.data.medium, high: slaRes.data.high, critical: slaRes.data.critical });
        setConfig(cfgRes.data);
        setAllCategories(catRes.data.categories || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const saveSla = async () => {
    setSaving(true);
    try {
      await ticketsApi.updateSlaConfig(slaConfig);
      toast.success('SLA configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await ticketsApi.updateConfig(config);
      toast.success('Ticket configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleCategory = (cat) => {
    setConfig(c => {
      const cats = c.enabled_categories || [];
      return { ...c, enabled_categories: cats.includes(cat) ? cats.filter(x => x !== cat) : [...cats, cat] };
    });
  };

  const runAutomation = async () => {
    setRunning(true);
    try {
      const res = await ticketsApi.runAutomation();
      const d = res.data;
      toast.success(`Automation complete: ${d.sla_breached} breached, ${d.auto_escalated} escalated, ${d.auto_closed} closed`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;

  return (
    <div className="space-y-5" data-testid="ticket-config-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 rounded-2xl p-6 text-white" data-testid="config-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
              <Gear className="h-6 w-6 text-indigo-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="config-heading">Ticket Configuration</h1>
              <p className="text-sm text-slate-300 mt-0.5">Configure SLA, automation rules, and categories</p>
            </div>
          </div>
          <Button onClick={runAutomation} disabled={running}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5 text-sm" data-testid="run-automation-btn">
            <Play className="h-4 w-4" weight="fill" /> {running ? 'Running...' : 'Run Automation Now'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* SLA Configuration */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="sla-config-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" /> SLA Hours per Priority
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">Set maximum resolution time (hours) for each priority level.</p>
            {[
              { key: 'critical', label: 'Critical', color: 'border-red-200 bg-red-50' },
              { key: 'high', label: 'High', color: 'border-orange-200 bg-orange-50' },
              { key: 'medium', label: 'Medium', color: 'border-amber-200 bg-amber-50' },
              { key: 'low', label: 'Low', color: 'border-slate-200 bg-slate-50' },
            ].map(p => (
              <div key={p.key} className={`flex items-center justify-between p-3 rounded-lg border ${p.color}`}>
                <span className="text-sm font-medium text-slate-700">{p.label}</span>
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} className="w-20 h-8 text-center text-sm border-slate-300"
                    value={slaConfig[p.key]} onChange={e => setSlaConfig(s => ({ ...s, [p.key]: parseInt(e.target.value) || 1 }))}
                    data-testid={`sla-${p.key}`} />
                  <span className="text-xs text-slate-500">hours</span>
                </div>
              </div>
            ))}
            <Button onClick={saveSla} disabled={saving} className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white"
              data-testid="save-sla-btn">
              {saving ? 'Saving...' : 'Save SLA Config'}
            </Button>
          </CardContent>
        </Card>

        {/* Automation Rules */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="automation-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
              <Lightning className="h-4 w-4 text-violet-500" /> Automation Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Auto-Escalation */}
            <div className="p-3 rounded-lg border border-violet-200 bg-violet-50 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Auto-Escalate Overdue</p>
                  <p className="text-[10px] text-slate-500">Automatically escalate tickets after SLA breach</p>
                </div>
                <Switch checked={config.auto_escalate_enabled}
                  onCheckedChange={v => setConfig(c => ({ ...c, auto_escalate_enabled: v }))}
                  data-testid="toggle-auto-escalate" />
              </div>
              {config.auto_escalate_enabled && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Escalate after</span>
                  <Input type="number" min={1} className="w-16 h-7 text-center text-xs border-slate-300"
                    value={config.auto_escalate_after_hours}
                    onChange={e => setConfig(c => ({ ...c, auto_escalate_after_hours: parseInt(e.target.value) || 1 }))}
                    data-testid="escalate-hours-input" />
                  <span className="text-xs text-slate-600">hours past SLA breach</span>
                </div>
              )}
            </div>

            {/* Auto-Close */}
            <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Auto-Close Resolved</p>
                  <p className="text-[10px] text-slate-500">Automatically close resolved tickets after inactivity</p>
                </div>
                <Switch checked={config.auto_close_enabled}
                  onCheckedChange={v => setConfig(c => ({ ...c, auto_close_enabled: v }))}
                  data-testid="toggle-auto-close" />
              </div>
              {config.auto_close_enabled && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Close after</span>
                  <Input type="number" min={1} className="w-16 h-7 text-center text-xs border-slate-300"
                    value={config.auto_close_after_days}
                    onChange={e => setConfig(c => ({ ...c, auto_close_after_days: parseInt(e.target.value) || 1 }))}
                    data-testid="close-days-input" />
                  <span className="text-xs text-slate-600">days with no response</span>
                </div>
              )}
            </div>

            <Button onClick={saveConfig} disabled={saving} className="w-full bg-gradient-to-r from-violet-500 to-indigo-600 text-white"
              data-testid="save-config-btn">
              {saving ? 'Saving...' : 'Save Automation Config'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Enabled Categories */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="categories-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
            <ArrowsClockwise className="h-4 w-4 text-indigo-500" /> Enabled Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">Select which ticket categories are available for users to choose when creating tickets.</p>
          <div className="flex flex-wrap gap-2">
            {allCategories.map(cat => {
              const enabled = (config.enabled_categories || []).includes(cat);
              return (
                <button key={cat} onClick={() => toggleCategory(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    enabled ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-slate-50 text-slate-400 border-slate-200'
                  }`} data-testid={`cat-toggle-${cat.replace(/[^a-zA-Z]/g, '-')}`}>
                  <CheckCircle className={`h-3.5 w-3.5 ${enabled ? 'text-indigo-500' : 'text-slate-300'}`} weight={enabled ? "fill" : "regular"} />
                  {cat}
                </button>
              );
            })}
          </div>
          <Button onClick={saveConfig} disabled={saving} className="mt-4 bg-gradient-to-r from-indigo-500 to-violet-600 text-white"
            data-testid="save-categories-btn">
            {saving ? 'Saving...' : 'Save Category Config'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketConfigPage;
